/**
 * 後台 — 系統設定(2026-10-06,第一層「結清原約」一起進)
 *
 * 把 app_config/main 裡客戶用得到的幾個開關做成表單,不必再請橙築改 Firestore:
 *   1. 會籍到期提醒:到期前幾天推播(#2);附「今天排程會推給誰」預覽,驗收用
 *   2. 久未互動名單:門檻天數、是否排除霽雲薈(#17)
 *   3. 會籍期間:升等時自動帶入的起算方式與月數(次月一日起 12 個月)
 *   4. 入會偏好問卷:開關(#16)
 *
 * 存檔走 PUT /api/admin/config(merge),只送這頁管的鍵,其他設定(客服群組等)不動。
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminHeader from "../../components/AdminHeader";
import { TIER_LABEL, type Tier } from "../../components/TierBadge";
import {
  adminTierExpiryPreview,
  getConfig,
  updateConfig,
  type TierExpiryPreview,
} from "../../api/admin";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";


export default function AdminSettings() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const showToast = useUIStore((s) => s.showToast);

  const [cfg, setCfg] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // 表單狀態(字串,存檔時才轉型)
  const [reminderDays, setReminderDays] = useState("30, 7, 1");
  const [sleepingDays, setSleepingDays] = useState("30");
  const [excludeGeneral, setExcludeGeneral] = useState(true);
  const [startMode, setStartMode] = useState<"next_month" | "today">("next_month");
  const [months, setMonths] = useState("12");
  const [surveyEnabled, setSurveyEnabled] = useState(true);

  const [preview, setPreview] = useState<TierExpiryPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    (async () => {
      try {
        // getConfig() 回的已經是 config 物件本身(api 層剝掉了 {config} 那層),不要再取 .config
        // (2026-10-06 上線後實機發現:畫面全是預設值、題目清單沒出來,就是這裡多拆了一層)
        const c = (await getConfig()) || {};
        setCfg(c);
        const days: unknown = c.expiry_reminder_days;
        setReminderDays(Array.isArray(days) ? days.join(", ") : "30, 7, 1");
        const churn = c.churn || {};
        setSleepingDays(String(churn.sleeping_days ?? 30));
        setExcludeGeneral(churn.sleeping_exclude_general ?? true);
        const period = c.membership_period || {};
        setStartMode(period.start_mode === "today" ? "today" : "next_month");
        setMonths(String(period.months ?? 12));
        setSurveyEnabled((c.join_survey || {}).enabled ?? true);
      } catch {
        showToast("載入設定失敗", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [role, navigate, showToast]);

  function parseDays(text: string): number[] | null {
    const nums = text
      .split(/[,，\s]+/)
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => parseInt(t, 10));
    if (nums.length === 0 || nums.some((n) => isNaN(n) || n < 1 || n > 365)) return null;
    return Array.from(new Set(nums)).sort((a, b) => b - a);
  }

  async function save() {
    const days = parseDays(reminderDays);
    if (!days) {
      showToast("到期提醒天數請填 1 到 365 的整數,用逗號分隔", "error");
      return;
    }
    const sd = parseInt(sleepingDays, 10);
    if (isNaN(sd) || sd < 1 || sd > 365) {
      showToast("久未互動門檻請填 1 到 365", "error");
      return;
    }
    const mo = parseInt(months, 10);
    if (isNaN(mo) || mo < 1 || mo > 60) {
      showToast("會籍月數請填 1 到 60", "error");
      return;
    }
    setBusy(true);
    try {
      await updateConfig({
        expiry_reminder_days: days,
        churn: { ...(cfg?.churn || {}), sleeping_days: sd, sleeping_exclude_general: excludeGeneral },
        membership_period: { ...(cfg?.membership_period || {}), start_mode: startMode, months: mo },
        join_survey: { ...(cfg?.join_survey || {}), enabled: surveyEnabled },
      });
      setReminderDays(days.join(", "));
      showToast("設定已儲存", "success");
      setPreview(null);
    } catch {
      showToast("儲存失敗,請稍後再試", "error");
    } finally {
      setBusy(false);
    }
  }

  async function loadPreview() {
    setPreviewLoading(true);
    try {
      setPreview(await adminTierExpiryPreview());
    } catch {
      showToast("載入預覽失敗", "error");
    } finally {
      setPreviewLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-cream">
        <AdminHeader active="settings" />
        <div className="p-10 text-center text-muted">載入中…</div>
      </div>
    );
  }

  const periodHint =
    startMode === "next_month"
      ? `升等當月的次月一日起算,${months || 12} 個月後的前一日到期(例:10 月升等,11/1 起至次年 10/31)`
      : `升等當日起算,${months || 12} 個月後的前一日到期`;

  return (
    <div className="min-h-screen bg-cream pb-12">
      <AdminHeader active="settings" />

      <div className="p-6 max-w-3xl space-y-5">
        <section className="card space-y-3">
          <div>
            <div className="text-sm font-bold text-ink">會籍到期提醒</div>
            <div className="text-xxs text-muted mt-0.5">
              每日 09:00 自動檢查,對潤雲薈與恆時薈會員在到期前幾天推 LINE 提醒,到期當日再推一次。同一位同一天只推一次。
            </div>
          </div>
          <div>
            <label className="label">到期前幾天提醒(逗號分隔)</label>
            <input className="input w-full max-w-xs" value={reminderDays} onChange={(e) => setReminderDays(e.target.value)} placeholder="30, 7, 1" />
          </div>
          <div className="pt-2 border-t border-line">
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted">今天排程會推給誰(不會真的推)</div>
              <button onClick={loadPreview} className="btn-secondary text-xs" disabled={previewLoading}>
                {previewLoading ? "載入中…" : "查看名單"}
              </button>
            </div>
            {preview && <ExpiryPreview data={preview} />}
          </div>
        </section>

        <section className="card space-y-3">
          <div>
            <div className="text-sm font-bold text-ink">久未互動會員</div>
            <div className="text-xxs text-muted mt-0.5">首頁「久未互動會員」名單與匯出使用的門檻。</div>
          </div>
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="label">超過幾天未互動</label>
              <input className="input w-28" type="number" min={1} max={365} value={sleepingDays} onChange={(e) => setSleepingDays(e.target.value)} />
            </div>
            <label className="flex items-center gap-2 text-sm text-ink pb-2">
              <input type="checkbox" checked={excludeGeneral} onChange={(e) => setExcludeGeneral(e.target.checked)} />
              排除霽雲薈(只看付費會員)
            </label>
          </div>
        </section>

        <section className="card space-y-3">
          <div>
            <div className="text-sm font-bold text-ink">會籍期間</div>
            <div className="text-xxs text-muted mt-0.5">後台把會員改為潤雲薈或恆時薈時自動帶入的期間;會員詳情頁仍可手動指定到期日。</div>
          </div>
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="label">起算方式</label>
              <select className="input" value={startMode} onChange={(e) => setStartMode(e.target.value as "next_month" | "today")}>
                <option value="next_month">次月一日起算</option>
                <option value="today">當日起算</option>
              </select>
            </div>
            <div>
              <label className="label">月數</label>
              <input className="input w-24" type="number" min={1} max={60} value={months} onChange={(e) => setMonths(e.target.value)} />
            </div>
          </div>
          <div className="text-xs text-muted">{periodHint}</div>
        </section>

        <section className="card space-y-3">
          <div>
            <div className="text-sm font-bold text-ink">入會偏好問卷</div>
            <div className="text-xxs text-muted mt-0.5">
              會員加入後在 LINE 聊天室推三題選擇題,答案寫成標籤(對象、品項、頻率),會員管理可依標籤篩選。
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={surveyEnabled} onChange={(e) => setSurveyEnabled(e.target.checked)} />
            加入後自動推送問卷
          </label>
          {Array.isArray(cfg?.join_survey?.questions) && cfg.join_survey.questions.length > 0 && (
            <ol className="text-xs text-muted list-decimal pl-5 space-y-0.5">
              {cfg.join_survey.questions.map((q: any) => (
                <li key={q.key}>
                  {q.title}
                  <span className="ml-1">({(q.options || []).map((o: any) => o.label).join(" / ")})</span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <div className="flex justify-end">
          <button onClick={save} className="btn-primary" disabled={busy}>
            {busy ? "儲存中…" : "儲存設定"}
          </button>
        </div>
      </div>
    </div>
  );
}


function ExpiryPreview({ data }: { data: TierExpiryPreview }) {
  const navigate = useNavigate();
  const blocks = [
    ...data.milestones.map((m) => ({ title: `${m.days} 天後到期`, rows: m.members })),
    { title: "今日到期", rows: data.just_expired },
  ];
  const total = blocks.reduce((n, b) => n + b.rows.length, 0);
  return (
    <div className="mt-3 space-y-3">
      {data.errors.length > 0 && (
        <div className="text-xs text-coral">查詢有錯誤:{data.errors.join(";")}</div>
      )}
      {total === 0 ? (
        <div className="text-xs text-muted">今天沒有需要提醒的會員。</div>
      ) : (
        blocks
          .filter((b) => b.rows.length > 0)
          .map((b) => (
            <div key={b.title}>
              <div className="text-xs font-medium text-ink mb-1">{b.title}({b.rows.length})</div>
              {b.rows.map((r) => (
                <button
                  key={r.line_user_id + b.title}
                  onClick={() => navigate(`/admin/members/${r.line_user_id}`)}
                  className="w-full text-left flex items-center justify-between py-1.5 border-b border-line last:border-0 hover:bg-cream/50 px-2 -mx-2 rounded text-sm"
                >
                  <span className="truncate">
                    {r.name || "—"}
                    <span className="ml-2 text-xs text-muted">{TIER_LABEL[r.tier as Tier] || r.tier}</span>
                  </span>
                  <span className="text-xs text-muted shrink-0 ml-3">
                    {r.membership_end ? String(r.membership_end).slice(0, 10) : "—"}
                    {r.already_sent && <span className="ml-2 text-sage">已推過</span>}
                  </span>
                </button>
              ))}
            </div>
          ))
      )}
    </div>
  );
}
